<template>
  <v-layout
    justify-center
    :pt-4="$vuetify.breakpoint.mdAndUp"
  >
    <v-flex
      xs10
      sm8
    >
      <form>
        <p class="required-block required-block_left">
          {{ $t('label.required_fields') }}
        </p>
        <h2 class="display-3 display-3_dark mb-0 py-0">
          {{ $t('message.family_data') }}
        </h2>
        <p
          class="mb-3"
          :class="[mobileScreen ? '' : 'f-1']"
        >
          {{ $t('message.fill_family_data') }}
        </p>
        <v-text-field
          v-model.trim="first_name"
          v-validate="validationRules.get('first_name_non_required')"
          :error-messages="errors.collect('first_name')"
          :label="$t('label.first_name')"
          :data-vv-as="$t('label.first_name')"
          data-vv-name="first_name"
        />
        <v-text-field
          v-model.trim="last_name"
          v-validate="validationRules.get('last_name')"
          class="required-field"
          :error-messages="errors.collect('last_name')"
          :label="$t('label.last_name')"
          :data-vv-as="$t('label.last_name')"
          data-vv-name="last_name"
          required
        />
        <v-autocomplete
          v-if="showProvince"
          ref="provinces"
          v-model="province"
          v-validate="validationRules.get('province')"
          :error-messages="errors.collect('province')"
          data-vv-name="province"
          :data-vv-as="$t('label.province')"
          :items="getProvinces"
          :search-input="provinceSearch"
          cache-items
          item-text="name"
          item-value="province_id"
          :label="$t('label.province')"
          class="required-field"
          append-icon="expand_more"
          :disabled="locationLoader || getProvinces.length <= 0"
          :loading="getProvinces.length <= 0"
        />
        <v-text-field
          v-model.trim="city"
          v-validate="validationRules.get('city')"
          class="required-field"
          :error-messages="errors.collect('city')"
          :label="$t('label.city_location')"
          data-vv-name="city"
          :data-vv-as="$t('label.city')"
          required
          :disabled="locationLoader"
        />
        <v-layout>
          <v-flex
            lg5
            md5
            xs12
            class="pr-md-4"
          >
            <v-text-field
              v-model="postal_code"
              v-validate="validationRules.get('postal_code')"
              class="required-field"
              mask="##-###"
              :error-messages="errors.collect('postal_code')"
              :label="$t('label.postal_code')"
              data-vv-name="postal_code"
              :data-vv-as="$t('label.postal_code')"
              required
              inputmode="numeric"
            />
          </v-flex>
        </v-layout>
        <v-text-field
          v-if="!no_street"
          v-model.trim="street"
          v-validate="validationRules.get('street')"
          class="required-field street-input-margin"
          :error-messages="errors.collect('street')"
          :label="$t('label.street')"
          data-vv-name="street"
          :data-vv-as="$t('label.street')"
          required
          :disabled="locationLoader"
        />
        <p v-if="!no_street">
          {{ $t('message.street_shortcut') }}
        </p>
        <v-checkbox
          v-model="no_street"
          :class="no_street ? '' : 'mt-0 pt-0'"
          :value="false"
          :label="$t('message.location_no_street')"
          data-vv-name="no_flat_number"
          type="checkbox"
          off-icon=""
          on-icon="check"
        />
        <v-layout justify-space-between>
          <v-flex
            lg5
            md5
            xs12
            :class="[mobileScreen ? 'mr-1' : '']"
          >
            <v-text-field
              v-model.trim="building_number"
              v-validate="validationRules.get('building_number')"
              class="required-field"
              :error-messages="errors.collect('building_number')"
              :label="$t('label.building_number')"
              data-vv-name="building_number"
              :data-vv-as="$t('label.building_number')"
              required
              :disabled="locationLoader"
            />
          </v-flex>
          <v-flex
            lg5
            md5
            xs12
          >
            <v-text-field
              v-model.trim="flat_number"
              v-validate="validationRules.get('flat_number')"
              :error-messages="errors.collect('flat_number')"
              :label="$t('label.flat_number')"
              data-vv-name="flat_number"
              :data-vv-as="$t('label.flat_number')"
              required
            />
          </v-flex>
        </v-layout>
        <div
          v-if="internal"
          class="area-title mt-2"
        >
          <v-input
            v-validate="validationRules.get('location')"
            data-vv-name="location"
            :data-vv-as="$t('label.area')"
            required
            :error-messages="errors.collect('location')"
            :value="locationName"
          >
            <template #prepend>
              {{ $t('label.area') }}
            </template>
            <v-progress-circular
              v-if="locationLoader"
              ml-3
              color="sz_red"
              :size="15"
              indeterminate
            />
            <p
              v-else-if="locationName"
              class="d-inline-block mb-0"
            >
              {{ locationName }}
            </p>
            <a
              v-else
              :disabled="locationLoader"
              @click="getLocation"
            >
              {{ $t('buttons.search_location') }}
            </a>
          </v-input>
        </div>
        <MultiplePhoneInput
          v-if="!no_number"
          ref="phonesInput"
          v-model="phones"
          class="mt-2"
        />
        <v-checkbox
          v-model="no_number"
          :error-messages="errors.collect('hasAgreed')"
          :value="false"
          :label="$t('message.family_has_no_phone')"
          data-vv-name="hasAgreed"
          :data-vv-as="$t('label.consent')"
          type="checkbox"
          off-icon=""
          on-icon="check"
        />
      </form>
      <v-layout
        v-if="isFormValid === false"
        mb-0
        mt-3
      >
        <v-flex>
          <div class="v-message text-xs-center">
            <div class="v-messages__message">
              {{ $t('errors.form_not_valid') }}
            </div>
          </div>
        </v-flex>
      </v-layout>
    </v-flex>
    <LocationDialog
      :value="locationDialog"
      internal
      @closeDialog="locationDialog = false"
      @acceptRejon="acceptRejon"
      @cancel="clearLocation"
      @addWithoutRejon="addWithoutRejon"
    />
    <FamilySimilarityCheck
      v-if="internal"
      ref="FamilySimilarityCheck"
      :add-family-callback="goToFamilySource"
      :for-decision-callback="goToFamilySource"
      :redirect-callback="goToSummary"
      :overwrite-family-edition-callback="overwriteFamilyEdition"
      @duplicateFound="clearValidationFlags"
    />
  </v-layout>
</template>

<script>
import MultiplePhoneInput from '@/components/widgets/form/MultiplePhoneInput';

import validationRules from '@/constants/validationRules';
import similarityCheckSteps from '@/constants/similarityCheckSteps';

import { mapFields } from 'vuex-map-fields';
import { mapGetters } from 'vuex';
import LocationDialog from '@/components/addFamily/internal/LocationDialog';

import httpStatusCode from 'http-status-codes';
import FamilySimilarityCheck from '@/components/widgets/addFamily/FamilySimilarityCheck';

import scrollToTopErrorMessage from '@/mixins/scrollToTopErrorMessage';

export default {
    name: 'AddFamilyForm',
    components: { FamilySimilarityCheck, LocationDialog, MultiplePhoneInput },
    mixins: [scrollToTopErrorMessage],
    inject: ['$validator'],
    props: {
        internal: {
            type: Boolean
        },
        edit: {
            type: Boolean
        },
        showProvince: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        ...mapFields('familyData', [
            'familyFormData.first_name',
            'familyFormData.last_name',
            'familyFormData.street',
            'familyFormData.building_number',
            'familyFormData.flat_number',
            'familyFormData.city',
            'familyFormData.province',
            'familyFormData.postal_code',
            'familyFormData.phones',
            'no_street'
        ]),
        ...mapGetters('provinces', ['getProvinces']),
        mobileScreen () {
            return this.$vuetify.breakpoint.xs;
        },
        location () {
            return this.$store.getters['familyData/getLocation'];
        },
        locationNode () {
            return this.location && this.location.node;
        },
        locationLoader () {
            return this.$store.getters['familyData/getLocationLoader'];
        },
        locationName () {
            if (this.getNoLocationByUser) {
                return this.$t('message.no_rejon');
            }

            return this.locationNode && this.locationNode.name;
        },
        getNoLocationByUser () {
            return this.$store.getters['familyData/getNoLocationByUser'];
        },
        isNative () {
            return this.location && this.location.native;
        }
    },
    data () {
        return {
            validationRules,
            httpStatusCode,
            similarityCheckSteps,
            no_number: false,
            provinceSearch: null,
            locationDialog: false,
            isFormValid: true
        };
    },
    watch: {
        no_number (val) {
            if (val) {
                this.$refs.phonesInput.clear();
            }
        },
        no_street (val) {
            if (val) {
                this.street = null;
            }
        },
        province (val, oldVal) {
            if (val !== oldVal) {
                this.clearLocation();
                this.clearUserDefault();
            }
        },
        city (val, oldVal) {
            if (val !== oldVal) {
                this.clearLocation();
                this.clearUserDefault();
            }
        },
        street (val, oldVal) {
            if (val !== oldVal) {
                this.clearLocation();
                this.clearUserDefault();
            }
        },
        building_number (val, oldVal) {
            if (val !== oldVal) {
                this.clearLocation();
                this.clearUserDefault();
            }
        },
        locationName (value) {
            this.$validator.validate('location', value);
        }
    },
    mounted () {
        if (this.edit) {
            this.no_number = this.phones.length <= 0;
        }
        if (this.$browserDetect.isSafari) {
            this.$refs.provinces.blur = () => {
                setTimeout(() => {
                    this.$refs.provinces.isMenuActive = false;
                    this.$refs.provinces.isFocused = false;
                    this.$refs.provinces.selectedIndex = -1;
                }, 0);
            };
        }
    },
    methods: {
        getLocation () {
            if (this.internal === false) {
                return;
            }
            const results = Promise.all([
                this.no_street || this.$validator.validate('street', this.street),
                this.$validator.validate('building_number', this.building_number),
                this.$validator.validate('city', this.city),
                this.$validator.validate('province', this.province)
            ]);
            results.then((result) => {
                if (result.includes(false) === false) {
                    const payload = {
                        buildingNumber: this.building_number,
                        city: this.city,
                        provinceId: this.province
                    };

                    if (!this.no_street) {
                        payload.street = this.street;
                    }

                    this.$store.commit('familyData/setNoLocationByUser', false);

                    this.$store
                        .dispatch('familyData/getLocation', payload)
                        .then((data) => {
                            this.locationDialog = true;
                        })
                        .catch((error) => {
                            const status = error.response && error.response.status;
                            if (status === this.httpStatusCode.BAD_REQUEST) {
                                this.locationDialog = true;
                            }
                        });
                }
            });
        },
        validate () {
            return this.$validator.validateAll();
        },
        submit () {
            this.$validator.validateAll().then((result) => {
                this.isFormValid = result;
                const checkLocation = this.locationNode != null || this.getNoLocationByUser === true;
                if (result) {
                    if (
                        this.location == null &&
            this.internal === true &&
            this.getNoLocationByUser === false
                    ) {
                        this.getLocation();
                    }
                    if (this.internal && checkLocation) {
                        if (this.isNative) {
                            this.$refs.FamilySimilarityCheck.init();
                        } else {
                            this.$router.push({ name: 'family-source' });
                        }
                    }
                    if (this.internal === false) {
                        this.$router.push({ name: 'family-information-external' });
                    }
                } else {
                    this.scrollToTopErrorMessage();
                }
            });
        },
        goToFamilySource () {
            const promise = new Promise((resolve, reject) => {
                this.$router.push({ name: 'family-source' });
                resolve();
            });
            return promise;
        },
        goToSummary () {
            this.$router.push({ name: 'family-summary' });
            this.$store.commit('familyData/setSimilarityParameters', {
                chosenOption: 2
            });
        },
        addWithoutRejon () {
            this.$store.commit('familyData/setNoLocationByUser', true);
            this.clearLocation();
        },
        clearLocation () {
            this.$store.commit('familyData/setLocation', null);
            this.$store.commit('familyData/setLocationLeaders', null);
        },
        clearUserDefault () {
            this.$store.commit('familyData/setNoLocationByUser', false);
        },
        acceptRejon () {
            this.locationDialog = false;
        },
        overwriteFamilyEdition () {
            return this.$store.dispatch('familyData/overwriteFamilyData');
        },
        clearValidationFlags () {
            Object.keys(this.$validator.flags).forEach((x) => {
                this.$validator.flags[x].changed = false;
            });
        }
    }
};
</script>

<style scoped></style>
